<template>
  <div class="container p-4">
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text" @click="router.push({ name: 'simce-hub' })" />
      <h3 class="text-xl font-bold m-0">Material SIMCE</h3>
    </div>

    <DjangoAuthGate hub-route="simce-hub" @authenticated="(ok) => (djangoOk = ok)" />

    <template v-if="djangoOk">
      <EstudyooLoadingBanner
        :visible="importando"
        :message="importandoDryRun ? 'Simulando importación (sin guardar)…' : 'Importando ítems desde Excel…'"
      />
      <TabView>
        <TabPanel header="Ítems SIMCE">
          <div class="flex flex-wrap gap-2 mb-3 align-items-end">
            <ButtonPrime label="Recargar" icon="pi pi-refresh" :loading="loadingItems" @click="cargarItems" />
            <div class="field m-0">
              <label class="text-sm block mb-1">Asignatura</label>
              <Dropdown
                v-model="filtroAsignatura"
                :options="asignaturas"
                option-label="nombre"
                option-value="id"
                placeholder="Todas"
                show-clear
                class="w-12rem"
                :loading="loadingCat"
                :disabled="loadingItems || importando"
                @change="cargarItems"
              />
            </div>
            <FileUpload
              mode="basic"
              choose-label="Importar Excel"
              accept=".xlsx,.xls"
              :auto="true"
              custom-upload
              :disabled="importando"
              @uploader="importar"
            />
            <FileUpload
              mode="basic"
              choose-label="Simular import (dry-run)"
              accept=".xlsx,.xls"
              :auto="true"
              custom-upload
              class="p-button-outlined"
              :disabled="importando"
              @uploader="(ev) => importar(ev, true)"
            />
          </div>
          <DataTable :value="items" :loading="loadingItems" class="p-datatable-sm" empty-message="Sin ítems">
            <Column field="titulo_corto" header="Título" />
            <Column header="Enunciado">
              <template #body="{ data }">
                {{ truncar(data.enunciado, 80) }}
              </template>
            </Column>
            <Column field="nivel_simce" header="Nivel" />
            <Column field="asignatura_id" header="Asignatura ID" />
            <Column header="Opciones">
              <template #body="{ data }">
                {{ (data.opciones || []).length }}
              </template>
            </Column>
            <Column field="activo" header="Activo" />
          </DataTable>
        </TabPanel>

        <TabPanel header="Opciones">
          <ButtonPrime label="Recargar" icon="pi pi-refresh" class="mb-3" :loading="loadingOpciones" @click="cargarOpciones" />
          <DataTable :value="opciones" :loading="loadingOpciones" class="p-datatable-sm">
            <Column field="item_id" header="Ítem ID" />
            <Column field="texto" header="Texto" />
            <Column field="es_correcta" header="Correcta" />
            <Column field="orden" header="Orden" />
          </DataTable>
        </TabPanel>

        <TabPanel header="Plantillas ensayo">
          <ButtonPrime label="Recargar" icon="pi pi-refresh" class="mb-3" :loading="loadingPlantillas" @click="cargarPlantillas" />
          <DataTable :value="plantillas" :loading="loadingPlantillas" class="p-datatable-sm">
            <Column field="nombre" header="Nombre" />
            <Column field="nivel_simce" header="Nivel" />
            <Column header="Asignatura">
              <template #body="{ data }">
                {{ data.asignatura?.nombre || '—' }}
              </template>
            </Column>
            <Column field="items_count" header="# Ítems" />
            <Column field="activa" header="Activa" />
          </DataTable>
        </TabPanel>

        <TabPanel header="Catálogo">
          <ButtonPrime label="Recargar catálogo" icon="pi pi-refresh" class="mb-3" :loading="loadingCat" @click="cargarCatalogo" />
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <h4 class="font-semibold mb-2">Asignaturas</h4>
              <DataTable :value="asignaturas" :loading="loadingCat" class="p-datatable-sm">
                <Column field="codigo" header="Código" />
                <Column field="nombre" header="Nombre" />
              </DataTable>
            </div>
            <div>
              <h4 class="font-semibold mb-2">Tipos de actividad</h4>
              <DataTable :value="tiposActividad" :loading="loadingCat" class="p-datatable-sm">
                <Column field="codigo" header="Código" />
                <Column field="nombre" header="Nombre" />
              </DataTable>
            </div>
            <div class="md:col-span-2">
              <h4 class="font-semibold mb-2">Ejes temáticos</h4>
              <DataTable :value="ejes" :loading="loadingCat" class="p-datatable-sm">
                <Column field="codigo" header="Código" />
                <Column field="nombre" header="Nombre" />
                <Column field="unidad_nombre" header="Unidad" />
                <Column field="capitulo_nombre" header="Capítulo" />
              </DataTable>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ButtonPrime from 'primevue/button';
import FileUpload from 'primevue/fileupload';
import Dropdown from 'primevue/dropdown';
import DjangoAuthGate from '@/components/estudyoo/DjangoAuthGate.vue';
import EstudyooLoadingBanner from '@/components/estudyoo/EstudyooLoadingBanner.vue';
import { SimceApi } from '@/services/SimceAdminApi';
import { isDjangoAdminAuthenticated } from '@/services/djangoAdminAuth';

const router = useRouter();
const toast = useToast();
const djangoOk = ref(isDjangoAdminAuthenticated());
const items = ref([]);
const opciones = ref([]);
const plantillas = ref([]);
const asignaturas = ref([]);
const ejes = ref([]);
const tiposActividad = ref([]);
const filtroAsignatura = ref(null);
const loadingItems = ref(false);
const loadingOpciones = ref(false);
const loadingPlantillas = ref(false);
const loadingCat = ref(false);
const importando = ref(false);
const importandoDryRun = ref(false);

function truncar(s, n) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

async function cargarItems() {
  loadingItems.value = true;
  try {
    const params = { limit: 200, activo: '1' };
    if (filtroAsignatura.value) params.asignatura_id = filtroAsignatura.value;
    items.value = await SimceApi.listItems(params);
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ítems', detail: e.response?.data?.detail || e.message, life: 5000 });
  } finally {
    loadingItems.value = false;
  }
}

async function cargarOpciones() {
  loadingOpciones.value = true;
  try {
    opciones.value = await SimceApi.listOpciones({ limit: 200 });
  } finally {
    loadingOpciones.value = false;
  }
}

async function cargarPlantillas() {
  loadingPlantillas.value = true;
  try {
    plantillas.value = await SimceApi.listPlantillas({ limit: 100 });
  } finally {
    loadingPlantillas.value = false;
  }
}

async function cargarCatalogo() {
  loadingCat.value = true;
  try {
    asignaturas.value = await SimceApi.listAsignaturas();
    tiposActividad.value = await SimceApi.listTiposActividad();
    ejes.value = await SimceApi.listEjes({ limit: 200 });
  } finally {
    loadingCat.value = false;
  }
}

async function importar(ev, dryRun = false) {
  const file = ev.files?.[0];
  if (!file) return;
  importando.value = true;
  importandoDryRun.value = dryRun;
  try {
    const r = await SimceApi.importarItemsXlsx(file, { plantilla: true, dryRun });
    const creados = r.items_creados ?? r.creados ?? 0;
    const actualizados = r.items_actualizados ?? r.actualizados ?? 0;
    toast.add({
      severity: dryRun ? 'info' : 'success',
      summary: dryRun ? 'Simulación import' : 'Importación SIMCE',
      detail: `Creados: ${creados}, actualizados: ${actualizados}${dryRun ? ' (sin guardar)' : ''}`,
      life: 7000,
    });
    if (!dryRun) await cargarItems();
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Importación',
      detail: e.response?.data?.detail || e.message,
      life: 6000,
    });
  } finally {
    importando.value = false;
    importandoDryRun.value = false;
    ev?.options?.clear?.();
  }
}

onMounted(() => {
  if (djangoOk.value) {
    cargarCatalogo();
    cargarItems();
    cargarOpciones();
    cargarPlantillas();
  }
});
</script>
